import { useEffect } from 'react';
import { Stack, styled, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SmallPhotoUploaderLayout, SmallFileUploaderLayout } from 'domain/shared/artifact/input';
import { CandidateResumeUploaderInput, ENGLISH_LEVEL_OPTIONS } from 'domain/shared/candidate';
import { EmployeePhotoUploaderInput } from 'domain/shared/employee';
import {
  AdditionalContactListInput,
  AdditionalContactInputListLayout,
  AdditionalContactWrapperProfile,
} from 'domain/shared/contact';
import {
  TextInput,
  CountryDropdown,
  Checkbox,
  EnumDropdown,
  NativeLanguageDropdown,
  TextArea,
  useFormContext,
} from 'framework/form';
import DualInputField from '../EditComponents/DualInputField/DualInputField';
import { SkillsInput, useSkillsService } from 'domain/recruiting/shared/skills';
import mixpanel from 'infrastructure/integrations/mixpanel';
import { isTrackedStatusSelected } from '../../statusChangeHelper';
import { CANDIDATE_TRACKED_STATUSES } from 'domain/shared/candidate';

const StyledCheckboxStack = styled(Box)(({ theme }: { theme: Theme }) => ({
  marginTop: '0 !important',
  display: 'flex',
  flexWrap: 'wrap',

  '& .MuiFormControl-root': {
    display: 'inline',
    width: 'fit-content',
  },
  '& .MuiFormControlLabel-labelPlacementEnd': { margin: '0' },

  '&>div': {
    marginBottom: 0,
    '& label': {
      marginRight: 0,
    },
  },
  '& .MuiCheckbox-root': {
    paddingRight: '2px',
  },
}));

const CandidateProfileEditFields = () => {
  const { data = {} } = useFormContext();
  const { searchByName } = useSkillsService();

  useEffect(() => {
    try {
      mixpanel.track('candidate_edit', {});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  }, []);

  return (
    <Stack direction="column" gap="0.5rem">
      <Stack direction="row" gap="0.5rem">
        <SmallPhotoUploaderLayout>
          <EmployeePhotoUploaderInput
            label=""
            source="photosIds"
            maxElements={1}
            uploadBtnText="Upload photo"
            showBtnImage={false}
          />
        </SmallPhotoUploaderLayout>

        <Stack
          direction="column"
          gap="0.5rem"
          sx={{
            width: '100%',
            '&>div': {
              marginBottom: 1,
            },
          }}
        >
          <TextInput source="name.fullName" label="Full name" />

          <CountryDropdown source="residenceCountry" label="" />

          <NativeLanguageDropdown source="nativeLanguage" label="" />

          <EnumDropdown
            source="englishLevel"
            label=""
            options={ENGLISH_LEVEL_OPTIONS}
            placeholder="Choose english level"
          />
        </Stack>
      </Stack>

      <StyledCheckboxStack>
        <Checkbox
          disabled={formData => formData.isInBlackList}
          source={CANDIDATE_TRACKED_STATUSES.KEEP_IN_VIEW.currentFlag}
          label={CANDIDATE_TRACKED_STATUSES.KEEP_IN_VIEW.label}
        />
        <Checkbox
          source={CANDIDATE_TRACKED_STATUSES.STUDENT.currentFlag}
          label={CANDIDATE_TRACKED_STATUSES.STUDENT.label}
        />

        <Checkbox
          disabled={formData => formData.isMarkedAsPotentialCandidate}
          source={CANDIDATE_TRACKED_STATUSES.BLACKLIST.currentFlag}
          label={CANDIDATE_TRACKED_STATUSES.BLACKLIST.label}
        />

        <Checkbox
          disabled={formData => formData.isInBlackList}
          source={CANDIDATE_TRACKED_STATUSES.REFERRAL.currentFlag}
          label={CANDIDATE_TRACKED_STATUSES.REFERRAL.label}
        />

        <Checkbox
          source={CANDIDATE_TRACKED_STATUSES.HIDDEN.currentFlag}
          label={CANDIDATE_TRACKED_STATUSES.HIDDEN.label}
        />
      </StyledCheckboxStack>

      {isTrackedStatusSelected(data) && (
        <TextArea source="statusChangeComment" label="Provide a reason for this status change" />
      )}

      <DualInputField
        primarySource="personalEmail"
        secondarySource="otherEmail"
        placeholder="Email"
        label=""
        trimComponent
      />

      <DualInputField
        primarySource="personalPhoneNumber"
        secondarySource="otherPhoneNumber"
        placeholder="Phone"
        label=""
      />

      <AdditionalContactWrapperProfile>
        <AdditionalContactListInput
          CustomInputListLayout={AdditionalContactInputListLayout}
          source="additionalContacts"
          maxFields={5}
        />
      </AdditionalContactWrapperProfile>

      <Box
        sx={{
          marginTop: '0 !important',
          '&>div': {
            marginBottom: '0px',
          },
          '& .dropdown__multi-value': {
            backgroundColor: 'primary.main',
            color: 'common.white',
            '&__label': {
              color: 'common.white',
            },
          },
        }}
      >
        <SkillsInput source="skills" label="" loadSuggestionsMethod={searchByName} />
      </Box>

      <SmallFileUploaderLayout>
        <CandidateResumeUploaderInput
          label=""
          source="lastUploadedResumeIds"
          maxElements={1}
          uploadBtnText="Upload or drag resume file"
        />
      </SmallFileUploaderLayout>
    </Stack>
  );
};

export default CandidateProfileEditFields;
